import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Dimensions,
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Text, type TextProps } from '../atoms/text/Text'
import { Icon } from '../atoms/Icon'
import { Theme } from '@theme'
import { computePopoverPosition, showApiErrorToast } from '@utils'
import { useFtTranslateText, type TranslateTextLanguage } from '@hooks'
import { useLanguage } from '@context'
import { TranslateLangPicker } from './TranslateLangPicker'

// Number of language rows in TranslateLangPicker (FR / EN / ES). An extra
// "original text" row is added when a translation is active.
const LANG_OPTIONS_COUNT = 3

interface TranslatableTextProps {
  /** The original text to display and translate from. */
  text: string
  /** Forwarded to the underlying <Text>. Defaults to "body". */
  type?: TextProps['type']
  textStyle?: StyleProp<TextStyle>
  textColor?: string
  numberOfLines?: number
  containerStyle?: StyleProp<ViewStyle>
}

/**
 * Renders text with a per-item translate toggle. Tap the icon to open a
 * contextual language picker (FR / EN / ES with flags). Tap the same lang
 * again — or "Original text" in the picker — to revert.
 *
 * Translations are cached locally per text+language so toggling is free
 * after the first call.
 */
export const TranslatableText: React.FC<TranslatableTextProps> = ({
  text,
  type = 'body',
  textStyle,
  textColor,
  numberOfLines,
  containerStyle,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)

  const triggerRef = useRef<View>(null)
  const [showPicker, setShowPicker] = useState(false)
  const [pickerPos, setPickerPos] = useState<{ x: number; y: number } | undefined>()
  const [activeLang, setActiveLang] = useState<TranslateTextLanguage | null>(null)
  const [cache, setCache] = useState<Partial<Record<TranslateTextLanguage, string>>>({})
  const { mutate: translateText, isPending } = useFtTranslateText()

  const displayed = useMemo(() => {
    if (activeLang && cache[activeLang]) return cache[activeLang]!
    return text
  }, [text, activeLang, cache])

  const onPickLang = useCallback(
    (lang: TranslateTextLanguage) => {
      if (lang === activeLang) {
        setActiveLang(null)
        return
      }
      if (cache[lang]) {
        setActiveLang(lang)
        return
      }
      translateText(
        { text, targetLanguage: lang },
        {
          onSuccess: (data) => {
            setCache((prev) => ({ ...prev, [lang]: data.translatedText }))
            setActiveLang(lang)
          },
          onError: (err) => {
            showApiErrorToast(t, err)
          },
        },
      )
    },
    [activeLang, cache, translateText, text, t],
  )

  const flattenedTextStyle = textStyle ? StyleSheet.flatten(textStyle) : undefined

  return (
    <View style={[styles.container, containerStyle]}>
      <Text
        type={type}
        color={textColor}
        style={[flattenedTextStyle, styles.text]}
        numberOfLines={numberOfLines}
      >
        {displayed}
      </Text>
      <View style={styles.toolbar}>
        <View ref={triggerRef} collapsable={false}>
          <TouchableOpacity
            accessibilityLabel={t('translate.toggle')}
            onPress={() => {
              triggerRef.current?.measureInWindow((x, y, _w, h) => {
                const { width: screenWidth, height: screenHeight } = Dimensions.get('window')
                // Keep the popover fully on screen: clamp to the right edge so
                // the labels are never clipped, and flip above the trigger when
                // there isn't enough room below it.
                const position = computePopoverPosition({
                  triggerX: x,
                  triggerY: y,
                  triggerHeight: h,
                  rowCount: LANG_OPTIONS_COUNT + (activeLang ? 1 : 0),
                  screenWidth,
                  screenHeight,
                })
                setPickerPos(position)
                setShowPicker(true)
              })
            }}
            style={[
              styles.iconBtn,
              activeLang ? { backgroundColor: theme.colors.primary + '20' } : null,
            ]}
          >
            {isPending ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
              <Icon
                name="translate"
                size={14}
                color={activeLang ? theme.colors.primary : theme.colors.lightGrey}
              />
            )}
          </TouchableOpacity>
        </View>
      </View>

      <TranslateLangPicker
        isVisible={showPicker}
        setIsVisible={setShowPicker}
        currentLang={activeLang}
        onPick={onPickLang}
        onRevert={() => setActiveLang(null)}
        position={pickerPos}
      />
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    // Inline layout: text flows, the translate icon sits at the END of the
    // message (bottom-right) so bubbles stay thin instead of having an extra row.
    container: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      flexShrink: 1,
    },
    text: {
      flexShrink: 1,
    },
    toolbar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      marginLeft: theme.spacing.xxs,
    },
    iconBtn: {
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
    },
  })
